import React, { useMemo, useState } from 'react';
import { 
  Table, TableBody, TableCell, TableHead, TableRow, 
  Box, ToggleButton, ToggleButtonGroup, Typography, Grow, Fade 
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import { colors, radii } from '../../utils/designTokens';

const formatDate = (dateString, includeYear = true) => {
  const options = { month: 'short', day: 'numeric', ...(includeYear && { year: 'numeric' }) };
  return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
};

const QuotaTable = ({ data, startDate, endDate }) => {
  const [view, setView] = useState('chart');
  const primaryChartColor = colors?.accent?.main || '#007AFF'; 

  const chartData = useMemo(() => {
    if (!startDate || !endDate) return [];
    
    const result = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); 
    
    let currentIndex = 0;

    while (start <= end) {
      const row = data?.[currentIndex];
      const isFuture = start > today;
      
      result.push({
        rawDate: new Date(start),
        formattedDate: formatDate(start, true),
        shortDate: formatDate(start, false),
        used_quota: isFuture ? null : (row?.used_quota || 0),
        remaining_quota: row?.remaining_quota || null,
      });

      start.setDate(start.getDate() + 1);
      currentIndex++;
    }
    return result;
  }, [startDate, endDate, data]);

  // Dynamically calculate headroom for the Y-Axis
  const yAxisMax = useMemo(() => {
    if (!chartData.length) return 5;
    const maxVal = Math.max(...chartData.map(d => d.used_quota || 0));
    // If 0 usage, default to 5. Otherwise, add 50% padding to the top, rounding up.
    // e.g., Max 5 -> Y-Axis goes to 8. Max 100 -> Y-Axis goes to 150.
    return maxVal === 0 ? 5 : Math.ceil(maxVal * 1.5);
  }, [chartData]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  if (!chartData.length) return null;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header & Toggle Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.neutral[800], letterSpacing: '-0.01em' }}>
          API Usage
        </Typography>
        
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="view toggle"
          sx={{
            bgcolor: colors.neutral[100],
            p: 0.5,
            borderRadius: '10px',
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              borderRadius: '8px !important',
              px: 1.5,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: colors.neutral[500],
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&.Mui-selected': {
                bgcolor: '#ffffff',
                color: colors.neutral[900],
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.04), 0px 1px 1px rgba(0, 0, 0, 0.02)',
                '&:hover': { bgcolor: '#ffffff' },
              },
              '&:hover': { bgcolor: colors.neutral[200] },
            },
          }}
        >
          <ToggleButton value="chart" aria-label="chart view">
            <ShowChartRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} /> Chart
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableRowsRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} /> Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart View */}
      {view === 'chart' && (
        <Fade in timeout={400}>
          <Box sx={{ width: '100%', height: 340 }}>
            <LineChart
              dataset={chartData}
              xAxis={[{ 
                dataKey: 'rawDate', 
                scaleType: 'time',
                valueFormatter: (date) => formatDate(date, false),
                // Force the layout engine to render approximately 7 evenly spaced ticks
                tickNumber: 7, 
              }]}
              yAxis={[{
                min: 0,
                max: yAxisMax, // Apply our dynamically calculated headroom
                tickMinStep: 1, // Prevent fractional request numbers (e.g., 2.5 requests)
              }]}
              series={[{
                dataKey: 'used_quota',
                label: 'Requests',
                color: primaryChartColor,
                area: true, 
                showMark: false, 
                curve: 'monotoneX', 
                connectNulls: false, 
              }]}
              margin={{ top: 20, bottom: 25, left: 0, right: 20 }}
              grid={{ horizontal: true }}
              sx={{
                '& .MuiChartsAxis-line': { stroke: 'transparent' },
                '& .MuiChartsAxis-tick': { stroke: 'transparent' },
                '& .MuiChartsAxis-tickLabel': { 
                  fill: colors.neutral[400], 
                  fontSize: '0.75rem', 
                  fontFamily: 'inherit',
                  fontWeight: 500,
                },
                '& .MuiChartsGrid-line': { 
                  strokeDasharray: '4 4', 
                  stroke: colors.neutral[200] 
                },
                '& .MuiLineElement-root': { 
                  strokeWidth: 3, 
                  filter: `drop-shadow(0px 6px 8px ${primaryChartColor}33)` 
                },
                '& .MuiAreaElement-root': { 
                  fill: 'url(#premium-gradient)' 
                },
              }}
              slotProps={{
                popper: {
                  sx: {
                    '& .MuiChartsTooltip-root': {
                      borderRadius: '12px',
                      boxShadow: '0 12px 32px -4px rgba(0,0,0,0.12), 0 4px 12px -2px rgba(0,0,0,0.04)',
                      border: `1px solid ${colors.neutral[200]}`,
                      padding: '8px 12px',
                      '& .MuiTypography-root': { fontFamily: 'inherit' }
                    }
                  }
                }
              }}
            >
              <defs>
                <linearGradient id="premium-gradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={primaryChartColor} stopOpacity={0.25} />
                  <stop offset="85%" stopColor={primaryChartColor} stopOpacity={0.0} />
                </linearGradient>
              </defs>
            </LineChart>
          </Box>
        </Fade>
      )}

      {/* Table View */}
      {view === 'table' && (
        <Grow in timeout={300} style={{ transformOrigin: 'top center' }}>
          <Box sx={{ overflowX: 'auto', border: `1px solid ${colors.neutral[200]}`, borderRadius: '15px' }}>
            <Table size="small" aria-label="API usage quota table">
              <TableHead sx={{ bgcolor: colors.neutral[50] }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: colors.neutral[600] }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: colors.neutral[600] }}>Used Quota</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: colors.neutral[600] }}>Remaining Quota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chartData.map((row) => {
                  if (row.used_quota === null && row.remaining_quota === null) return null;

                  return (
                    <TableRow 
                      key={row.rawDate.toISOString()}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { bgcolor: colors.neutral[50] } 
                      }}
                    >
                      <TableCell>{row.formattedDate}</TableCell>
                      <TableCell style={{ width: 160 }} align="right">
                        {row.used_quota !== null ? row.used_quota : 0}
                      </TableCell>
                      <TableCell style={{ width: 160 }} align="right">
                        {row.remaining_quota !== null ? row.remaining_quota : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Grow>
      )}
    </Box>
  );
};

export default QuotaTable;


// Changes to the code above - Moved the heavy date generation loop into a useMemo hook so it only computes when the dates change, fixed the typo in the formatting function, and replaced the array index with a stable date.toISOString() key to prevent React reconciliation bugs.

// Previous version with minor issues:
// import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
// import React from 'react';
// import { colors } from '../../utils/designTokens';

// const formaTableCellate = (dateString) => {
//   const options = { year: 'numeric', month: 'short', day: 'numeric' };
//   const date = new Date(dateString);
//   return date.toLocaleDateString('en-US', options);
// };

// const QuotaTable = ({ data, startDate, endDate }) => {
//   const dates = [];
//   const start = new Date(startDate);
//   const end = new Date(endDate);

//   // Generate an array of dates between start and end dates
//   while (start <= end) {
//     dates.push(new Date(start));
//     start.setDate(start.getDate() + 1);
//   }

//   return (
//     <Table size="small" aria-label="usage-quota">
//       <TableHead>
//         <TableRow>
//           <TableCell>Date</TableCell>
//           <TableCell align="right">Used Quota</TableCell>
//           <TableCell align="right">Remaining Quota</TableCell>
//         </TableRow>
//       </TableHead>
//       <TableBody>
//         {dates.map((date, index) => {
//           const row = data?.[index];
//           if (!row) return null;
//           return (
//             <TableRow sx={{
//               '&:nth-of-type(odd)': { backgroundColor: colors.neutral[50] },
//               '&:last-child td, &:last-child th': { border: 0 },
//             }} key={index}>
//               <TableCell>{formaTableCellate(date)}</TableCell>
//               <TableCell style={{ width: 160 }} align="right">{row.used_quota}</TableCell>
//               <TableCell style={{ width: 160 }} align="right">{row.remaining_quota}</TableCell>
//             </TableRow>
//           );
//         })}
//       </TableBody>
//     </Table >
//   );
// };

// export default QuotaTable;
